import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { CalendarPlus, X, Trash } from "@phosphor-icons/react";

const STATUS_CHIP = {
  pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800", cancelled: "bg-slate-100 text-slate-500",
};
const fmtD = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });
const label = (r) => (r.half_day ? `${fmtD(r.from_date)} · half day` : r.from_date === r.to_date ? fmtD(r.from_date) : `${fmtD(r.from_date)} → ${fmtD(r.to_date)}`);
const todayIn = (tz) => new Date().toLocaleDateString("en-CA", { timeZone: tz });

export default function PunchLeave({ tz }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/punch/leave"); setData(d); } catch { /* shown as empty */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (r) => {
    if (!window.confirm(`Cancel your leave request for ${label(r)}?`)) return;
    try { await api.delete(`/punch/leave/${r.id}`); toast.success("Request cancelled"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const rows = data?.requests || [];
  return (
    <div className="mt-6" data-testid="punch-leave">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">Leave {data?.pending ? `· ${data.pending} pending` : ""}</div>
        <button onClick={() => setOpen(true)} className="h-8 px-3 rounded-full bg-[#0F172A] text-white text-[12px] font-semibold flex items-center gap-1.5 active:scale-[0.98] transition-transform" data-testid="punch-leave-request-btn">
          <CalendarPlus size={14} weight="bold" /> Request leave
        </button>
      </div>
      <div className="rounded-sm border border-slate-200 bg-white divide-y divide-slate-100" data-testid="punch-leave-list">
        {data && rows.length === 0 && <div className="px-3 py-3 text-[12px] text-slate-400 text-center" data-testid="punch-leave-empty">No leave requests yet. All leave is unpaid (LOP); Sundays &amp; holidays are paid.</div>}
        {rows.map((r) => (
          <div key={r.id} className="px-3 py-2 flex items-start gap-2" data-testid={`punch-leave-row-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[12.5px] font-semibold text-slate-900">{label(r)}</span>
                <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider ${STATUS_CHIP[r.status]}`} data-testid={`punch-leave-status-${r.id}`}>{r.status}</span>
              </div>
              <div className="text-[11px] text-slate-500 truncate">{Number(r.days) % 1 ? r.days : Number(r.days)} day{Number(r.days) === 1 ? "" : "s"} · {r.reason}</div>
              {r.admin_note && <div className="text-[11px] text-slate-600 italic">Admin: “{r.admin_note}”</div>}
            </div>
            {r.status === "pending" && <button onClick={() => cancel(r)} className="h-7 w-7 rounded-full border border-slate-200 flex items-center justify-center text-slate-500" title="Cancel request" data-testid={`punch-leave-cancel-${r.id}`}><Trash size={13} /></button>}
          </div>
        ))}
      </div>
      {open && <LeaveForm tz={tz} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function LeaveForm({ tz, onClose, onDone }) {
  const t = todayIn(tz);
  const [form, setForm] = useState({ from_date: t, to_date: t, half_day: false, reason: "" });
  const [busy, setBusy] = useState(false);
  const single = form.from_date === form.to_date;
  const set = (k, v) => setForm((f) => {
    const n = { ...f, [k]: v };
    if (k === "from_date" && n.to_date < v) n.to_date = v;
    if (n.from_date !== n.to_date) n.half_day = false;
    return n;
  });

  const submit = async () => {
    if (form.reason.trim().length < 3) return toast.error("Please give a short reason");
    setBusy(true);
    try {
      const { data } = await api.post("/punch/leave", form);
      toast.success(`Leave request sent to admin · ${Number(data.days) % 1 ? data.days : Number(data.days)} day${Number(data.days) === 1 ? "" : "s"}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/55 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="punch-leave-form">
      <div className="w-full max-w-[480px] bg-white rounded-t-xl sm:rounded-sm p-5 pb-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#EA580C]">Leave request</div>
            <div className="text-lg font-bold">Apply for leave</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full border border-slate-200 flex items-center justify-center" data-testid="punch-leave-form-close"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1">From</div>
            <input type="date" value={form.from_date} onChange={(e) => set("from_date", e.target.value)} className="w-full h-11 rounded-sm border border-slate-300 px-2 text-[14px]" data-testid="punch-leave-from" />
          </label>
          <label className="block">
            <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1">To</div>
            <input type="date" value={form.to_date} min={form.from_date} onChange={(e) => set("to_date", e.target.value)} className="w-full h-11 rounded-sm border border-slate-300 px-2 text-[14px]" data-testid="punch-leave-to" />
          </label>
        </div>
        <label className={`mt-3 flex items-center gap-2 text-[13px] ${single ? "text-slate-800" : "text-slate-400"}`}>
          <input type="checkbox" checked={form.half_day} disabled={!single} onChange={(e) => set("half_day", e.target.checked)} className="h-4 w-4" data-testid="punch-leave-half-day" />
          Half day {single ? "(counts as ½ day)" : "— only for a single date"}
        </label>
        <label className="block mt-3">
          <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1">Reason</div>
          <textarea rows={3} value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="e.g. Family function in hometown" className="w-full rounded-sm border border-slate-300 px-2 py-2 text-[14px]" data-testid="punch-leave-reason" />
        </label>
        <div className="text-[11px] text-slate-500 mt-2">Sundays &amp; holidays in the range are skipped automatically. Leave is unpaid (LOP) and shows in your salary slip once approved.</div>
        <button onClick={submit} disabled={busy} className="mt-4 w-full h-12 rounded-sm bg-[#EA580C] text-white font-semibold text-[15px] disabled:opacity-50 active:scale-[0.99] transition-transform" data-testid="punch-leave-submit">
          {busy ? "Sending…" : "Send request"}
        </button>
      </div>
    </div>
  );
}
